/**
 * Shared behaviour for the covaso-lp-* landing sections, loaded once and
 * cached instead of an inline <script> per section.
 *
 * Accordions — <details data-lp-accordion>, in 01-lp-hero and 08-lp-faq.
 *   Opening AND closing animate. Once this script has run, a details gets
 *   data-anim and the is-expanded class drives its grid rows; `open` is only
 *   toggled around it. A closed <details> never renders its content, so on
 *   open we let one frame paint it collapsed (0fr) before expanding — without
 *   that the very first open snaps instead of animating. Without JS an open
 *   <details> simply shows its content (see the CSS in those sections).
 *   One delegated listener covers every accordion, including sections the
 *   theme editor re-renders.
 *
 * Demo videos — [data-lp-autopause] video, in 03-lp-demo.
 *   Muted autoplay loops keep decoding off screen; pause them there.
 */

const CLOSE_MS = 230; // matches the grid-template-rows transition in the sections

/** @param {ParentNode} root */
function prepareAccordions(root) {
  root.querySelectorAll('details[data-lp-accordion]:not([data-anim])').forEach((element) => {
    const details = /** @type {HTMLDetailsElement} */ (element);
    details.setAttribute('data-anim', '');
    if (details.open) {
      details.classList.add('is-expanded');
      details.dataset.lpWant = 'open';
    }
  });
}

/** @param {HTMLDetailsElement} details */
function toggle(details) {
  if (details.dataset.lpWant === 'open') {
    details.dataset.lpWant = 'closed';
    details.classList.remove('is-expanded');
    setTimeout(() => {
      if (details.dataset.lpWant === 'closed') details.removeAttribute('open');
    }, CLOSE_MS);
    return;
  }

  details.dataset.lpWant = 'open';
  details.setAttribute('open', '');
  const expand = () => {
    if (details.dataset.lpWant === 'open') details.classList.add('is-expanded');
  };
  requestAnimationFrame(() => requestAnimationFrame(expand));
  setTimeout(expand, 80); // in case rAF is throttled
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const summary = target.closest('summary');
  const details = summary?.parentElement;
  if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-lp-accordion][data-anim]')) return;
  event.preventDefault();
  toggle(details);
});

/** @type {IntersectionObserver | null} */
const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = /** @type {HTMLVideoElement} */ (entry.target);
            if (entry.isIntersecting) video.play().catch(() => {});
            else video.pause();
          });
        },
        { threshold: 0.1 }
      )
    : null;

/** @param {ParentNode} root */
function prepareVideos(root) {
  if (!videoObserver) return;
  root.querySelectorAll('[data-lp-autopause] video:not([data-lp-observed])').forEach((video) => {
    video.setAttribute('data-lp-observed', '');
    videoObserver.observe(video);
  });
}

/** @param {ParentNode} root */
function prepare(root) {
  prepareAccordions(root);
  prepareVideos(root);
}

prepare(document);

// Sections re-rendered by the theme editor arrive without data-anim.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) prepare(event.target);
});
